import { useState } from "react";
import { Star, GripVertical, ChevronUp, ChevronDown, X, EyeOff, ArrowUpRight } from "lucide-react";

// Éditeur partagé (préférences utilisateur et menus par défaut des rôles).
export default function MenuPrefsEditor({ items, prefs, onChange, onOpenItem, testPrefix = "menu" }) {
  const [drag, setDrag] = useState(null);
  const hidden = prefs.hidden || [];
  const favs = prefs.favorites || [];
  const favIds = new Set(favs.map((f) => f.id));

  const setFavs = (f) => onChange({ ...prefs, favorites: f });
  const toggleHidden = (id) => onChange({ ...prefs, hidden: hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id] });
  const toggleFav = (i) => setFavs(favIds.has(i.id) ? favs.filter((f) => f.id !== i.id) : [...favs, { id: i.id, label: i.label, href: i.href }]);
  const move = (from, to) => {
    if (to < 0 || to >= favs.length || from === to) return;
    const f = [...favs]; const [x] = f.splice(from, 1); f.splice(to, 0, x); setFavs(f);
  };

  const Row = ({ i }) => (
    <div className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 text-sm">
      <input type="checkbox" checked={!hidden.includes(i.id)} onChange={() => toggleHidden(i.id)} data-testid={`${testPrefix}-pref-${i.id}`} className="w-4 h-4 accent-[#007AFF]" />
      <span className={`flex-1 truncate ${hidden.includes(i.id) ? "text-zinc-500" : "text-zinc-200"}`}>{i.label}</span>
      <button type="button" onClick={() => toggleFav(i)} data-testid={`${testPrefix}-fav-${i.id}`} title={favIds.has(i.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
        className={`p-1 rounded transition-colors ${favIds.has(i.id) ? "text-[#FFB020]" : "text-zinc-600 hover:text-[#FFB020]"}`}>
        <Star className="w-4 h-4" fill={favIds.has(i.id) ? "currentColor" : "none"} />
      </button>
      {onOpenItem && hidden.includes(i.id) && i.href && (
        <button type="button" onClick={() => onOpenItem(i)} data-testid={`${testPrefix}-open-${i.id}`} className="p-1 rounded text-zinc-400 hover:text-white" title="Ouvrir"><ArrowUpRight className="w-4 h-4" /></button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div data-testid={`${testPrefix}-favorites`}>
        <p className="text-xs uppercase tracking-wider text-[#FFB020] mb-2 flex items-center gap-1.5"><Star className="w-3.5 h-3.5" /> Favoris en haut du menu ({favs.length})</p>
        {favs.length === 0 ? <p className="text-xs text-zinc-600 px-2">Cliquez sur l'étoile d'un onglet pour l'ajouter.</p> : favs.map((f, idx) => (
          <div key={f.id} draggable onDragStart={() => setDrag(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => { move(drag, idx); setDrag(null); }}
            data-testid={`${testPrefix}-favrow-${f.id}`}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded border text-sm mb-1 cursor-grab ${drag === idx ? "border-[#FFB020]/60 bg-[#FFB020]/10" : "border-white/5 bg-zinc-900/60"}`}>
            <GripVertical className="w-4 h-4 text-zinc-500 shrink-0" />
            <span className="flex-1 truncate text-zinc-200">{f.label}</span>
            <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0} data-testid={`${testPrefix}-favup-${f.id}`} className="p-0.5 text-zinc-500 hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
            <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === favs.length - 1} data-testid={`${testPrefix}-favdown-${f.id}`} className="p-0.5 text-zinc-500 hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
            <button type="button" onClick={() => setFavs(favs.filter((x) => x.id !== f.id))} className="p-0.5 text-zinc-500 hover:text-[#FF3B30]"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="space-y-0.5" data-testid={`${testPrefix}-visible-list`}>
        {items.filter((i) => !hidden.includes(i.id)).map((i) => <Row key={i.id} i={i} />)}
      </div>
      <div className="pt-3 border-t border-white/10" data-testid={`${testPrefix}-hidden-section`}>
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><EyeOff className="w-3.5 h-3.5" /> Masqués ({items.filter((i) => hidden.includes(i.id)).length})</p>
        {items.filter((i) => hidden.includes(i.id)).map((i) => <Row key={i.id} i={i} />)}
      </div>
    </div>
  );
}
